'use client'

import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { HOME_LIST, HOME_ROW, HOME_SOFT_SPRING } from '@/features/home/components/home-motion'
import type { Gauge, GaugeInput } from '@/features/home/lib/home-gauges'
import { useNumberFormat } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'

const CX = 60
const CY = 60
const R = 48

/** A point on the dial for a 0–100 value (0 = far left, 100 = far right). */
function point(value: number, radius = R): [number, number] {
  const angle = Math.PI * (1 - value / 100)
  return [CX + radius * Math.cos(angle), CY - radius * Math.sin(angle)]
}

function arc(from: number, to: number): string {
  const [x1, y1] = point(from)
  const [x2, y2] = point(to)
  return `M${x1},${y1} A${R},${R} 0 0 1 ${x2},${y2}`
}

const DIRECTIONAL = [
  { from: 0, to: 41, className: 'stroke-chart-red/70' },
  { from: 43, to: 57, className: 'stroke-foreground/25' },
  { from: 59, to: 100, className: 'stroke-chart-mint/70' },
]
const HEAT = [
  { from: 0, to: 24, className: 'stroke-foreground/20' },
  { from: 26, to: 54, className: 'stroke-shell-accent/50' },
  { from: 56, to: 79, className: 'stroke-ox-gold/70' },
  { from: 81, to: 100, className: 'stroke-chart-red/70' },
]

function InputLine({ input }: { input: GaugeInput }) {
  const t = useTranslations('trade.home.brief.gauges.inputs')
  const fmt = useNumberFormat()
  const values: Record<string, string | number> = { ...input.params }
  for (const key of ['total', 'longs', 'shorts'] as const) {
    if (key in input.params) {
      values[key] = fmt.number(Math.abs(Number(input.params[key])), {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      })
    }
  }
  if ('total' in input.params) values.sign = Number(input.params.total) >= 0 ? '+' : '−'
  if ('direction' in input.params) values.direction = t(`directions.${input.params.direction}`)
  return (
    <li className="flex items-start gap-2">
      <span
        className={cn(
          'mt-1.5 size-1.5 shrink-0 rounded-full',
          input.key === 'headlines'
            ? 'bg-ox-gold'
            : input.contribution > 0.15
              ? 'bg-chart-mint'
              : input.contribution < -0.15
                ? 'bg-chart-red'
                : 'bg-foreground/40'
        )}
      />
      <span>{t(input.key, values)}</span>
    </li>
  )
}

function Dial({ gauge }: { gauge: Gauge }) {
  const t = useTranslations('trade.home.brief.gauges')
  const segments = gauge.topic === 'news' ? HEAT : DIRECTIONAL
  const score = gauge.score
  const [tipX, tipY] = point(score ?? 50, R - 14)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.li
          variants={HOME_ROW}
          className="bg-foreground/[0.03] hover:bg-foreground/[0.05] border-foreground/10 flex cursor-default flex-col items-center gap-0.5 rounded-md border px-3 pt-2 pb-2.5 transition-colors"
        >
          <span className="text-foreground/60 self-start text-xs">
            {t(`topics.${gauge.topic}`)}
          </span>
          <svg viewBox="0 0 120 68" className="w-full max-w-36" aria-hidden="true">
            {segments.map((segment) => (
              <path
                key={segment.from}
                d={arc(segment.from, segment.to)}
                fill="none"
                strokeWidth={7}
                strokeLinecap="round"
                className={cn(segment.className, score === null && 'stroke-foreground/10')}
              />
            ))}
            {score !== null && (
              <motion.line
                x1={CX}
                y1={CY}
                initial={{ x2: point(50, R - 14)[0], y2: point(50, R - 14)[1] }}
                animate={{ x2: tipX, y2: tipY }}
                transition={HOME_SOFT_SPRING}
                strokeWidth={2.5}
                strokeLinecap="round"
                className="stroke-foreground"
              />
            )}
            <circle cx={CX} cy={CY} r={4} className="fill-foreground" />
          </svg>
          <span className="text-foreground font-display -mt-1 text-xl leading-6 tracking-tight">
            {score ?? '—'}
          </span>
          <span
            className={cn(
              'text-[11px]',
              gauge.reading === 'bullish' || gauge.reading === 'leanBullish'
                ? 'text-chart-mint'
                : gauge.reading === 'bearish' || gauge.reading === 'leanBearish'
                  ? 'text-chart-red'
                  : gauge.reading === 'hot' || gauge.reading === 'busy'
                    ? 'text-ox-gold'
                    : 'text-foreground/50'
            )}
          >
            {gauge.reading ? t(`readings.${gauge.reading}`) : t('noData')}
          </span>
        </motion.li>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">
        <p className="mb-1.5 font-medium">{t(`help.${gauge.topic}`)}</p>
        {gauge.inputs.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {gauge.inputs.map((input) => (
              <InputLine key={`${input.key}:${String(input.params.asset ?? '')}`} input={input} />
            ))}
          </ul>
        ) : (
          <p className="opacity-70">{t('noData')}</p>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * The brief's four topic gauges — derivatives, flow, sentiment, news — each a dial with its
 * 0–100 score and a word, the inputs behind it on hover (home-gauges.ts).
 */
export function HomeGauges({
  gauges,
  className,
}: {
  gauges: readonly Gauge[]
  className?: string
}) {
  return (
    <motion.ul
      variants={HOME_LIST}
      initial="hidden"
      animate="show"
      className={cn('grid grid-cols-2 gap-2 md:grid-cols-4', className)}
    >
      {gauges.map((gauge) => (
        <Dial key={gauge.topic} gauge={gauge} />
      ))}
    </motion.ul>
  )
}
